import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Accessibility, X, Type, Contrast, BookOpenText, PauseCircle, Link2, Crosshair } from "lucide-react";

const DEFAULTS = { text: 0, contrast: false, dyslexia: false, motion: false, links: false, focus: false };

function applyState(s) {
  const root = document.documentElement;
  root.classList.toggle("a11y-text-lg", s.text === 1);
  root.classList.toggle("a11y-text-xl", s.text === 2);
  root.classList.toggle("a11y-contrast", s.contrast);
  root.classList.toggle("a11y-dyslexia", s.dyslexia);
  root.classList.toggle("a11y-reduce-motion", s.motion);
  root.classList.toggle("a11y-links", s.links);
  root.classList.toggle("a11y-cursor", s.focus);
}

export function WcagWidget() {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState(() => {
    try {
      return { ...DEFAULTS, ...JSON.parse(localStorage.getItem("dpo-a11y") || "{}") };
    } catch {
      return DEFAULTS;
    }
  });

  useEffect(() => {
    applyState(state);
    localStorage.setItem("dpo-a11y", JSON.stringify(state));
  }, [state]);

  const toggle = (key) => setState((s) => ({ ...s, [key]: !s[key] }));
  const textLabel = ["Text size: standard", "Text size: large", "Text size: extra large"][state.text];

  const ToggleRow = ({ icon: Icon, label, active, onClick, testid }) => (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-pressed={active}
      className={`flex w-full items-center justify-between gap-3 rounded-lg border px-3.5 py-3 text-left text-sm font-medium transition-colors duration-200 ${
        active ? "border-ember bg-orange-50 text-navy" : "border-slate-200 bg-slate-50 text-slate-600 hover:border-slate-400"
      }`}
    >
      <span className="flex items-center gap-2.5">
        <Icon className="h-4 w-4 text-ember" aria-hidden="true" /> {label}
      </span>
      <span className={`h-5 w-9 rounded-full p-0.5 transition-colors ${active ? "bg-ember" : "bg-slate-300"}`} aria-hidden="true">
        <span className={`block h-4 w-4 rounded-full bg-white shadow transition-transform ${active ? "translate-x-4" : ""}`} />
      </span>
    </button>
  );

  return (
    <div className="fixed bottom-5 left-5 z-50" data-testid="wcag-widget">
      {open && (
        <div
          className="mb-3 w-80 max-w-[calc(100vw-2.5rem)] rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl"
          role="dialog"
          aria-label="Accessibility options"
          data-testid="wcag-panel"
        >
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-display text-base font-bold text-navy">Accessibility Options</h2>
            <button onClick={() => setOpen(false)} aria-label="Close accessibility options" data-testid="wcag-close-button" className="text-slate-400 hover:text-navy">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="space-y-2.5">
            <ToggleRow icon={Type} label={textLabel} active={state.text > 0} testid="wcag-toggle-text-size" onClick={() => setState((s) => ({ ...s, text: (s.text + 1) % 3 }))} />
            <ToggleRow icon={Contrast} label="High contrast" active={state.contrast} testid="wcag-toggle-contrast" onClick={() => toggle("contrast")} />
            <ToggleRow icon={BookOpenText} label="Dyslexia-friendly font" active={state.dyslexia} testid="wcag-toggle-dyslexia" onClick={() => toggle("dyslexia")} />
            <ToggleRow icon={PauseCircle} label="Reduce motion" active={state.motion} testid="wcag-toggle-motion" onClick={() => toggle("motion")} />
            <ToggleRow icon={Link2} label="Highlight links" active={state.links} testid="wcag-toggle-links" onClick={() => toggle("links")} />
            <ToggleRow icon={Crosshair} label="Large cursor" active={state.focus} testid="wcag-toggle-cursor" onClick={() => toggle("focus")} />
          </div>
          <button
            type="button"
            onClick={() => setState(DEFAULTS)}
            data-testid="wcag-reset-button"
            className="mt-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-xs font-medium text-slate-500 transition-colors hover:border-slate-400 hover:text-navy"
          >
            Reset all options
          </button>
          <Link
            to="/accessibility"
            onClick={() => setOpen(false)}
            data-testid="wcag-statement-link"
            className="mt-3 block text-center text-xs font-semibold text-hydro hover:underline"
          >
            Read our Accessibility Statement
          </Link>
        </div>
      )}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-label="Open accessibility options"
        aria-expanded={open}
        data-testid="wcag-widget-button"
        className="flex items-center justify-center rounded-full bg-hydro p-3 text-white shadow-lg shadow-hydro/30 transition-transform duration-200 hover:scale-110"
      >
        <Accessibility className="h-6 w-6" />
      </button>
    </div>
  );
}
